import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getEvent, checkout } from '../api';
import { useAuth } from '../context/AuthContext';

const categoryIcons = {
  conference: '🎤', workshop: '🛠️', concert: '🎵', 
  sports: '⚽', networking: '🤝', other: '📅'
};

function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { loadEvent(); }, [id]);

  const loadEvent = async () => {
    const { data } = await getEvent(id);
    setEvent(data);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
  };

  const availableTickets = event ? event.capacity - (event.ticketsSold || 0) : 0;
  const soldOut = availableTickets <= 0;
  const totalAmount = event ? event.price * quantity : 0;

  const handleCheckout = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setProcessing(true);
    try {
      const { data } = await checkout({ eventId: id, quantity, paymentMethod });
      setSuccess(data);
      setShowPayment(false);
      loadEvent();
      refreshUser();
    } catch (err) {
      alert(err.response?.data?.message || 'Booking failed');
    }
    setProcessing(false);
  };

  if (!event) return <div className="container"><p>Loading...</p></div>;

  return (
    <div>
      <Link to="/" className="btn btn-secondary" style={{ marginBottom: '25px' }}>← Back to Events</Link>
      
      {success && (
        <div className="alert alert-success">
          <h3>🎉 Booking Confirmed!</h3>
          <p>Transaction ID: <strong>{success.payment.transactionId}</strong></p>
          <p>Event: {success.payment.eventTitle}</p>
          <p>Tickets: {success.payment.quantity} × {formatPrice(success.payment.pricePerTicket)} = <strong>{formatPrice(success.payment.totalAmount)}</strong></p>
        </div>
      )}

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div className="event-image" style={{ height: '280px', borderRadius: '0' }}>
          <span style={{ fontSize: '6rem' }}>{categoryIcons[event.category] || '📅'}</span>
          <span className="event-category">{event.category}</span>
          {event.featured && <span className="event-featured">⭐ Featured</span>}
        </div>
        
        <div style={{ padding: '35px' }}>
          <h1 style={{ marginBottom: '20px' }}>{event.title}</h1>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' }}>
            <div className="info-card">
              <p className="info-label">📅 Date & Time</p>
              <p className="info-value">
                {new Date(event.date).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                {event.time && ` at ${event.time}`}
              </p>
            </div>
            <div className="info-card">
              <p className="info-label">📍 Location</p>
              <p className="info-value">{event.location}</p>
            </div>
            <div className="info-card">
              <p className="info-label">🎫 Availability</p>
              <p className="info-value">{availableTickets} of {event.capacity} tickets left</p>
            </div>
          </div>

          <h3 style={{ marginBottom: '10px' }}>About this event</h3>
          <p style={{ color: '#64748b', lineHeight: '1.8', marginBottom: '30px' }}>{event.description}</p>

          <div className="booking-section">
            <div className="price-display">
              <p className="price-label">Price per ticket</p>
              <p className="price-amount">{formatPrice(event.price)}</p>
            </div>
            {soldOut ? (
              <div className="btn btn-secondary" style={{ cursor: 'not-allowed' }}>Sold Out</div>
            ) : (
              <button 
                onClick={() => user ? setShowPayment(true) : navigate('/login')} 
                className="btn btn-primary btn-lg"
              >
                {user ? 'Book Tickets' : 'Login to Book'}
              </button>
            )}
          </div>
        </div>
      </div>

      {showPayment && (
        <div className="modal-overlay" onClick={() => setShowPayment(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Book Tickets</h2>
            <p style={{ color: '#64748b', marginBottom: '25px' }}>{event.title}</p>
            
            <div className="form-group">
              <label>Number of Tickets</label>
              <div className="quantity-selector">
                <button 
                  type="button" 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="qty-btn"
                >−</button>
                <span className="qty-value">{quantity}</span>
                <button 
                  type="button" 
                  onClick={() => setQuantity(Math.min(10, Math.min(availableTickets, quantity + 1)))}
                  className="qty-btn"
                >+</button>
              </div>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                Max 10 tickets per booking • {availableTickets} available
              </p>
            </div>

            <div className="payment-summary">
              <div className="payment-item">
                <span>Event</span>
                <span style={{ fontWeight: '600' }}>{event.title}</span>
              </div>
              <div className="payment-item">
                <span>Date</span>
                <span>{new Date(event.date).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="payment-item">
                <span>Price per ticket</span>
                <span>{formatPrice(event.price)}</span>
              </div>
              <div className="payment-item">
                <span>Quantity</span>
                <span>× {quantity}</span>
              </div>
              <div className="payment-item payment-total-row">
                <span>Total Amount</span>
                <span className="payment-total">{formatPrice(totalAmount)}</span>
              </div>
            </div>

            <div className="form-group">
              <label>Payment Method</label>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                {[
                  { id: 'card', label: '💳 Card' },
                  { id: 'upi', label: '📱 UPI' },
                  { id: 'netbanking', label: '🏦 Net Banking' }
                ].map(method => (
                  <button
                    key={method.id}
                    type="button"
                    className={`category-btn ${paymentMethod === method.id ? 'active' : ''}`}
                    onClick={() => setPaymentMethod(method.id)}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', marginTop: '25px' }}>
              <button 
                onClick={handleCheckout} 
                className="btn btn-success" 
                style={{ flex: 1 }}
                disabled={processing}
              >
                {processing ? 'Processing...' : `Pay ${formatPrice(totalAmount)}`}
              </button>
              <button onClick={() => setShowPayment(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetails;
